import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEffectiveAuth } from '@/hooks/useEffectiveAuth';
import { useSocialWall, FeedFilter, ReactionFilter } from '@/hooks/useSocialWall';
import { useReactionAllowances } from '@/hooks/useReactionAllowances';
import { usePinPost } from '@/hooks/usePinPost';
import PostComposer from '@/components/social-wall/PostComposer';
import WallPostCard from '@/components/social-wall/WallPostCard';
import { WallNotificationBell } from '@/components/social-wall/WallNotificationBell';
import ReactionLeaderboard from '@/components/social-wall/ReactionLeaderboard';
import { CommunityGoalWidget } from '@/components/social-wall/CommunityGoalWidget';
import { StreakDisplay } from '@/components/social-wall/StreakDisplay';
import { StreakBadge } from '@/components/social-wall/StreakBadge';
import DraftBoard from '@/components/social-wall/DraftBoard';
import FeaturedLanes from '@/components/social-wall/FeaturedLanes';
import BoostTokenDisplay from '@/components/social-wall/BoostTokenDisplay';
import WallReactionAnalytics from '@/components/social-wall/WallReactionAnalytics';
import TrendingHashtags from '@/components/social-wall/TrendingHashtags';
import { ReactionFilterChips, getEmptyStateMessage } from '@/components/social-wall/ReactionFilterChips';
import { useUserStreaks } from '@/hooks/useUserStreaks';
import { useBoostTokens } from '@/hooks/useBoostTokens';
import { Button } from '@/components/ui/button';
import { Loader2, Users, Trophy, TrendingUp, Award, Filter, Compass } from 'lucide-react';
import { LabsWelcomeAlert } from '@/components/labs/LabsWelcomeAlert';

type ViewMode = 'feed' | 'draft';

const FILTER_STORAGE_KEY = 'social_wall_filter';

// Helper component to show best streak badge in header
const StreakBadgeDisplay: React.FC<{ profileId: string }> = ({ profileId }) => {
  const { getBestStreak, loading } = useUserStreaks(profileId);
  const bestStreak = getBestStreak();
  
  if (loading || !bestStreak || bestStreak.current_streak <= 0) return null;
  
  return (
    <StreakBadge 
      streakCount={bestStreak.current_streak}
      streakType={bestStreak.streak_type as any}
      size="sm"
    />
  );
};

const SocialWall: React.FC = () => {
  const { profile } = useEffectiveAuth();
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('feed');
  const [reactionFilter, setReactionFilter] = useState<ReactionFilter>(null);
  
  // Load filter from localStorage or default to 'all'
  const [filter, setFilter] = useState<FeedFilter>(() => {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY);
    return (saved === 'following' ? 'following' : 'all') as FeedFilter;
  });

  const {
    posts,
    isLoading,
    hasMore,
    isLoadingMore,
    loadMore,
    createPost,
    isCreating,
    toggleReaction,
    reportPost,
    followingCount,
  } = useSocialWall(profile?.id, filter, reactionFilter);

  // Reaction allowances for limiting special reactions
  const { allowances, useAllowance, restoreAllowance } = useReactionAllowances(profile?.id);

  // Pin post functionality
  const { pinPost, unpinPost } = usePinPost(profile?.id);

  // Boost tokens
  const { tokens: boostTokens, boostPost } = useBoostTokens(profile?.id);

  // Wrap toggleReaction to check and use allowances
  const handleReaction = useCallback(async (postId: string, reactionType: string, hasReaction: boolean, postAuthorId: string) => {
    // If removing a reaction, restore allowance
    if (hasReaction) {
      toggleReaction({ postId, reactionType, hasReaction, postAuthorId });
      restoreAllowance(reactionType);
      return;
    }
    
    // If adding a reaction, check and use allowance
    const canUse = await useAllowance(reactionType);
    if (!canUse) return; // Toast shown by useAllowance
    
    toggleReaction({ postId, reactionType, hasReaction, postAuthorId });
  }, [toggleReaction, useAllowance, restoreAllowance]);

  // Persist filter to localStorage and clear reaction filter when changing main filter
  const handleFilterChange = (newFilter: FeedFilter) => {
    setFilter(newFilter);
    setReactionFilter(null); // Clear reaction filter when main filter changes
    localStorage.setItem(FILTER_STORAGE_KEY, newFilter);
  };

  // Handle reaction filter change
  const handleReactionFilterChange = (newReactionFilter: ReactionFilter) => {
    setReactionFilter(newReactionFilter);
  };

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  // Set up Intersection Observer for infinite scroll
  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      {
        rootMargin: '200px',
        threshold: 0,
      }
    );

    if (loadMoreTriggerRef.current) {
      observerRef.current.observe(loadMoreTriggerRef.current);
    }
  }, [hasMore, isLoadingMore, loadMore]);

  useEffect(() => {
    setupObserver();
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setupObserver]);

  return (
    <div className="max-w-4xl mx-auto">
      <LabsWelcomeAlert className="mb-6" />
      <div className="flex gap-6">
        {/* Main Feed Column */}
        <div className="flex-1 max-w-2xl">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-bold">Social Wall</h1>
                <p className="text-muted-foreground">Share updates with the community</p>
              </div>
              {profile && <StreakBadgeDisplay profileId={profile.id} />}
            </div>
            <div className="flex items-center gap-2">
              <Link to="/athlete-dashboard/wall/discover">
                <Button variant="outline" size="sm" className="gap-2">
                  <Compass className="h-4 w-4" />
                  <span className="hidden sm:inline">Discover</span>
                </Button>
              </Link>
              {profile && <WallReactionAnalytics profileId={profile.id} />}
              {profile && <BoostTokenDisplay profileId={profile.id} />}
              <button
                onClick={() => setShowLeaderboard(!showLeaderboard)}
                className={`p-2 rounded-lg transition-colors lg:hidden ${
                  showLeaderboard ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                }`}
                title="Leaderboards"
              >
                <Trophy className="h-5 w-5" />
              </button>
              {profile && <WallNotificationBell profileId={profile.id} />}
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="mb-4 flex gap-1 p-1 bg-muted rounded-lg w-fit">
            <button
              onClick={() => setViewMode('feed')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'feed'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Feed
            </button>
            <button
              onClick={() => setViewMode('draft')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                viewMode === 'draft'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Award className="h-4 w-4" />
              Draft Board
            </button>
          </div>

          {viewMode === 'draft' ? (
            <DraftBoard />
          ) : (
            <>
              {/* Feed Filter Toggle */}
              <div className="mb-4 flex gap-1 p-1 bg-muted rounded-lg w-fit">
                <button
                  onClick={() => handleFilterChange('all')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    filter === 'all'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => handleFilterChange('following')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                    filter === 'following'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Users className="h-4 w-4" />
                  Following
                </button>
                <button
                  onClick={() => handleFilterChange('trending')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                    filter === 'trending'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <TrendingUp className="h-4 w-4" />
                  Trending
                </button>
              </div>

              {/* Reaction Filter Chips */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Filter by reaction:</span>
                </div>
                <ReactionFilterChips
                  activeFilter={reactionFilter}
                  onFilterChange={handleReactionFilterChange}
                />
              </div>

              {profile && (
                <PostComposer
                  userAvatar={profile.profile_photo_url}
                  userName={profile.name}
                  profileId={profile.id}
                  gradYear={profile.grad_year}
                  sport={profile.sport_id ? undefined : undefined}
                  position={profile.primary_position}
                  onSubmit={createPost}
                  isSubmitting={isCreating}
                />
              )}

              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {reactionFilter ? (
                    (() => {
                      const emptyState = getEmptyStateMessage(reactionFilter);
                      return (
                        <div>
                          <p className="font-medium">{emptyState.title}</p>
                          <p className="text-sm mt-1">{emptyState.subtitle}</p>
                        </div>
                      );
                    })()
                  ) : filter === 'following' ? (
                    followingCount === 0 ? (
                      <div>
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">You're not following anyone yet</p>
                        <p className="text-sm mt-1">Switch to All to discover athletes.</p>
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium">No posts from athletes you follow yet</p>
                        <p className="text-sm mt-1">Check back later or switch to All.</p>
                      </div>
                    )
                  ) : filter === 'trending' ? (
                    <div>
                      <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="font-medium">No trending posts right now</p>
                      <p className="text-sm mt-1">Posts become trending when they get lots of different reactions!</p>
                    </div>
                  ) : (
                    <p>No posts yet. Be the first to share something!</p>
                  )}
                </div>
              ) : (
                <div>
                  {posts.map((post) => (
                    <WallPostCard
                      key={post.id}
                      post={post}
                      onReaction={(postId, reactionType, hasReaction) => 
                        handleReaction(postId, reactionType, hasReaction, post.profiles?.id || '')
                      }
                      onReport={(postId, reason, details) => reportPost({ postId, reason, details })}
                      onPinPost={pinPost}
                      onUnpinPost={unpinPost}
                      currentUserId={profile?.id}
                      currentUserAvatar={profile?.profile_photo_url}
                      currentUserName={profile?.name}
                      reactionAllowances={allowances}
                      tokenBalance={boostTokens?.balance || 0}
                      onBoostPost={boostPost}
                    />
                  ))}

                  {/* Infinite scroll trigger */}
                  <div ref={loadMoreTriggerRef} className="h-1" />

                  {/* Loading more indicator */}
                  {isLoadingMore && (
                    <div className="flex justify-center py-6">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {/* End of feed message */}
                  {!hasMore && posts.length > 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      <p>You've reached the end</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar - Desktop */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <div className="sticky top-4 space-y-4">
            {profile && (
              <div className="bg-card rounded-lg border p-4">
                <StreakDisplay profileId={profile.id} variant="full" />
              </div>
            )}
            <TrendingHashtags />
            <FeaturedLanes />
            <CommunityGoalWidget />
            <ReactionLeaderboard />
          </div>
        </div>
      </div>
      
      {/* Mobile Overlay */}
      {showLeaderboard && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 lg:hidden" onClick={() => setShowLeaderboard(false)}>
          <div className="absolute right-4 top-20 w-80 max-w-[calc(100%-2rem)] max-h-[70vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()}>
            <TrendingHashtags />
            <FeaturedLanes />
            <CommunityGoalWidget />
            <ReactionLeaderboard />
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialWall;
